// Authentication dialog for login and signup
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import {
  maybeAutoStartKakaoLogin,
  resetPassword,
  signInWithEmail,
  signInWithGoogle,
  signInWithKakao,
  signUpWithEmail,
} from "@/lib/firebase";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FcGoogle } from "react-icons/fc";
import { SiKakaotalk } from "react-icons/si";
import { z } from "zod";

const loginSchema = z.object({
  email: z.string().email("올바른 이메일을 입력해주세요"),
  password: z.string().min(6, "비밀번호는 최소 6자 이상이어야 합니다"),
});

const signupSchema = loginSchema
  .extend({
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "비밀번호가 일치하지 않습니다",
    path: ["confirmPassword"],
  });

interface AuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function getFirebaseErrorMessage(error: any): string {
  const code = error?.code;

  switch (code) {
    case "auth/invalid-credential":
      // Firebase v9+ 에서는 user-not-found와 wrong-password가 invalid-credential로 통합됨
      return "이메일 또는 비밀번호가 올바르지 않습니다. 다시 확인해주세요.";
    case "auth/user-not-found":
      return "가입되지 않은 이메일입니다. 회원가입을 진행해주세요.";
    case "auth/wrong-password":
      return "비밀번호가 올바르지 않습니다. 비밀번호를 잊으셨다면 재설정해주세요.";
    case "auth/invalid-email":
      return "올바른 이메일 형식이 아닙니다.";
    case "auth/user-disabled":
      return "이 계정은 비활성화되었습니다. 고객센터에 문의해주세요.";
    case "auth/email-already-in-use":
      return "이미 사용 중인 이메일입니다.";
    case "auth/weak-password":
      return "비밀번호가 너무 약합니다. 6자 이상 입력해주세요.";
    case "auth/operation-not-allowed":
      return "이메일/패스워드 로그인이 활성화되지 않았습니다. 관리자에게 문의해주세요.";
    case "auth/too-many-requests":
      return "로그인 시도가 너무 많습니다. 잠시 후 다시 시도해주세요.";
    case "auth/network-request-failed":
      return "네트워크 연결을 확인한 뒤 다시 시도해주세요.";
    default:
      // 에러 코드를 포함하여 디버깅에 도움
      console.error("[AuthDialog] Unknown Firebase error:", { code, error });
      return (
        error?.message ||
        `문제가 발생했습니다. (코드: ${code || "unknown"}) 다시 시도해주세요.`
      );
  }
}

export function AuthDialog({ open, onOpenChange }: AuthDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"login" | "signup">("login");
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const { toast } = useToast();

  // 새 탭으로 열린 경우 자동으로 카카오 로그인 시작
  useEffect(() => {
    maybeAutoStartKakaoLogin();
  }, []);

  const loginForm = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const signupForm = useForm<z.infer<typeof signupSchema>>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      await signInWithGoogle();
      onOpenChange(false);
      toast({
        title: "로그인 성공",
        description: "환영합니다!",
      });
    } catch (error: any) {
      toast({
        title: "로그인 실패",
        description: getFirebaseErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKakaoSignIn = async () => {
    try {
      setIsLoading(true);
      await signInWithKakao();
      onOpenChange(false);
      toast({
        title: "카카오 로그인",
        description: "카카오에서 로그인을 계속해주세요.",
      });
    } catch (error: any) {
      toast({
        title: "로그인 실패",
        description: error.message || "다시 시도해주세요",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailLogin = async (values: z.infer<typeof loginSchema>) => {
    try {
      setIsLoading(true);
      console.log("[AuthDialog] Attempting email login:", {
        email: values.email.substring(0, 5) + "...",
      });

      // Sign in with Firebase - AuthContext will handle /api/me automatically
      await signInWithEmail(values.email, values.password);

      console.log("[AuthDialog] Firebase sign-in successful");

      // Close dialog and show success message
      onOpenChange(false);
      toast({
        title: "로그인 성공",
        description: "환영합니다!",
      });
    } catch (error: any) {
      console.error("[AuthDialog] Login error:", {
        code: error?.code,
        message: error?.message,
        error,
      });
      toast({
        title: "로그인 실패",
        description: getFirebaseErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSignup = async (values: z.infer<typeof signupSchema>) => {
    try {
      setIsLoading(true);
      await signUpWithEmail(values.email, values.password);
      onOpenChange(false);
      toast({
        title: "회원가입 성공",
        description: "OZK News에 오신 것을 환영합니다!",
      });
    } catch (error: any) {
      toast({
        title: "회원가입 실패",
        description: getFirebaseErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      toast({
        title: "이메일을 입력해주세요",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      await resetPassword(resetEmail.trim());
      toast({
        title: "비밀번호 재설정 이메일 발송",
        description: "이메일을 확인하여 비밀번호를 재설정해주세요.",
      });
      setShowPasswordReset(false);
      setResetEmail("");
    } catch (error: any) {
      toast({
        title: "재설정 실패",
        description: getFirebaseErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="dialog-auth">
        <DialogHeader>
          <DialogTitle>
            {activeTab === "login" ? "로그인" : "회원가입"}
          </DialogTitle>
          <DialogDescription>
            {activeTab === "login"
              ? "로그인하고 작성, 답변 알림, 저장과 참여 기능을 이어서 이용하세요."
              : "OZK 계정을 만들고 호주 생활 정보와 커뮤니티 기능을 이용하세요."}
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as "login" | "signup")}
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login" data-testid="tab-login">
              로그인
            </TabsTrigger>
            <TabsTrigger value="signup" data-testid="tab-signup">
              회원가입
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login" className="space-y-4">
            <Button
              variant="outline"
              className="w-full"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              data-testid="button-google-signin"
            >
              <FcGoogle className="mr-2 h-5 w-5" />
              Google로 계속하기
            </Button>

            <Button
              variant="outline"
              className="w-full"
              onClick={handleKakaoSignIn}
              disabled={isLoading}
              data-testid="button-kakao-signin"
              style={{
                backgroundColor: "#FEE500",
                color: "#000000",
                borderColor: "#FEE500",
              }}
            >
              <SiKakaotalk className="mr-2 h-5 w-5" />
              카카오톡으로 계속하기
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  또는
                </span>
              </div>
            </div>

            <Form {...loginForm}>
              <form
                onSubmit={loginForm.handleSubmit(handleEmailLogin)}
                className="space-y-4"
              >
                <FormField
                  control={loginForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>이메일</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="your@email.com"
                          {...field}
                          data-testid="input-login-email"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={loginForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>비밀번호</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="••••••"
                          {...field}
                          data-testid="input-login-password"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordReset(true);
                      setResetEmail(loginForm.getValues("email") || "");
                    }}
                    className="text-sm text-primary hover:underline font-medium"
                  >
                    비밀번호 찾기
                  </button>
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                  data-testid="button-login-submit"
                >
                  {isLoading && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  로그인
                </Button>
              </form>
            </Form>

            {/* 비밀번호 재설정 폼 */}
            {showPasswordReset && (
              <div className="mt-4 p-4 border rounded-lg bg-muted/50">
                <h4 className="text-sm font-semibold mb-2">비밀번호 재설정</h4>
                <form onSubmit={handlePasswordReset} className="space-y-3">
                  <Input
                    type="email"
                    placeholder="이메일 주소"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      size="sm"
                      disabled={isLoading}
                      className="flex-1"
                    >
                      {isLoading && (
                        <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                      )}
                      재설정 이메일 보내기
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowPasswordReset(false);
                        setResetEmail("");
                      }}
                      disabled={isLoading}
                    >
                      취소
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </TabsContent>

          <TabsContent value="signup" className="space-y-4">
            <Button
              variant="outline"
              className="w-full"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              data-testid="button-google-signup"
            >
              <FcGoogle className="mr-2 h-5 w-5" />
              Google로 계속하기
            </Button>

            <Button
              variant="outline"
              className="w-full"
              onClick={handleKakaoSignIn}
              disabled={isLoading}
              data-testid="button-kakao-signup"
              style={{
                backgroundColor: "#FEE500",
                color: "#000000",
                borderColor: "#FEE500",
              }}
            >
              <SiKakaotalk className="mr-2 h-5 w-5" />
              카카오톡으로 계속하기
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  또는
                </span>
              </div>
            </div>

            <Form {...signupForm}>
              <form
                onSubmit={signupForm.handleSubmit(handleEmailSignup)}
                className="space-y-4"
              >
                <FormField
                  control={signupForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>이메일</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="your@email.com"
                          {...field}
                          data-testid="input-signup-email"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={signupForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>비밀번호</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="••••••"
                          {...field}
                          data-testid="input-signup-password"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={signupForm.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>비밀번호 확인</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="••••••"
                          {...field}
                          data-testid="input-signup-confirm-password"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                  data-testid="button-signup-submit"
                >
                  {isLoading && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  회원가입
                </Button>
              </form>
            </Form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
